import CheckIcon from 'assets/icons/check.svg?react';
import { useTranslation } from 'react-i18next';
import { Button, Link } from '@spotted-zebra-uk/ui-components';
import Icon from '@/components/atoms/Icon';
import { useSupportEmail } from '@/contexts/SupportEmail/SupportEmailContext';
import styles from './PracticeCompletion.module.scss';

interface PracticeCompletionProps {
  avatarName: string;
  remainingDemos: number | null;
  /**
   * Set for candidates who declared a difficulty with hearing or speaking. The
   * practice run is the first time they find out whether a spoken interview
   * works for them, so this is the moment to repeat the offer to arrange
   * something else — before they commit to the real interview.
   */
  shouldContactUs: boolean;
  onStartInterview: () => void;
  onGoAgain: () => void;
  onBackToChooseInterviewer: () => void;
}

export function PracticeCompletion({
  avatarName,
  remainingDemos,
  shouldContactUs,
  onStartInterview,
  onGoAgain,
}: PracticeCompletionProps) {
  const { t } = useTranslation();
  const { supportEmail, supportEmailHref } = useSupportEmail();
  const hasRemainingDemos = remainingDemos != null && remainingDemos > 0;

  return (
    <div className={styles.practiceCompletion}>

      <div className={styles.content}>
        <div className={styles.checkCircle}>
          <CheckIcon className={styles.checkIcon} />
        </div>

        <h1 className={styles.title}>{t('aiInterview.practiceComplete')}</h1>

        <p className={styles.description}>
          {t('aiInterview.practiceDescription', { avatarName })}
          {!shouldContactUs && (
            <>
              <br />
              {t('aiInterview.readyToBegin')}
            </>
          )}
        </p>

        {shouldContactUs && (
          <div
            className={styles.contactUsNotice}
            data-testid="practice-completion__contact-us"
          >
            <p className={styles.contactUsTitle}>
              {t('aiInterview.practiceContactUs.title')}
            </p>
            <p className={styles.contactUsBody}>
              {t('aiInterview.practiceContactUs.body')}
            </p>
            <Link
              to={supportEmailHref}
              target="_blank"
              aria-label={t('common.sendEmailTo', {
                email: supportEmail,
              })}
              data-testid="practice-completion__support-email-link"
            >
              {supportEmail}
            </Link>
          </div>
        )}

        <div className={styles.actions}>
          <Button
            onClick={onStartInterview}
            rightIcon={<Icon icon="right_arrow" />}
          >
            {t('aiInterview.startInterviewNow')}
          </Button>

          {hasRemainingDemos && (
            <button
              type="button"
              onClick={onGoAgain}
              className={styles.goAgainLink}
            >
              {t('aiInterview.goAgain')}
              {remainingDemos !== null && t('aiInterview.sessionsLeft', { count: remainingDemos })}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
